// src/pages/sessions/components/LiveSession/layout/SessionBottomBar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import MediaControls from '../Controls/MediaControls';
import { 
  LockClosedIcon,
  Cog6ToothIcon,
  CheckIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  EyeSlashIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

const SessionBottomBar = ({
  audioEnabled,
  videoEnabled,
  screenSharing,
  embeddedDocument,
  isWhiteboardActive,
  isWhiteboardDetached,
  videoToggling,
  canToggleCamera,
  canToggleMicrophone,
  cameraLockEnabled,
  microphoneLockEnabled,
  hideOwnVideo,
  isTeacher,
  sidebarCollapsed,
  roomConnected,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onToggleDocumentShare,
  onToggleWhiteboard,
  onToggleMediaLock,
  onToggleHideOwnVideo,
  onToggleSidebar,
  userMutedAudio,
  userMutedVideo,
  hasUserInteracted,
  isTablet = false,
  unreadMessages = 0,
  autoAssignBreakoutRooms = false,
  onToggleAutoAssignBreakoutRooms,
}) => {
  // Tablet: slightly larger touch targets so buttons are easier to hit
  const btnPad = isTablet ? 'p-4' : 'p-3';
  const { t } = useTranslation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
    };
    if (settingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [settingsOpen]);

  const getLockText = () => {
    if (cameraLockEnabled && microphoneLockEnabled) return t('controls.locks.cameraMicLocked');
    if (cameraLockEnabled) return t('controls.locks.cameraLocked');
    return t('controls.locks.micLocked');
  };

  return (
    <div
      className="bg-gray-800 border-t border-gray-700 px-4 pt-3 flex-shrink-0 z-20"
      style={{
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        minHeight: 'calc(env(safe-area-inset-bottom) + 68px)',
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MediaControls
            audioEnabled={audioEnabled}
            videoEnabled={videoEnabled}
            screenSharing={screenSharing}
            embeddedDocument={embeddedDocument}
            videoToggling={videoToggling}
            canToggleCamera={canToggleCamera}
            canToggleMicrophone={canToggleMicrophone}
            cameraLocked={!isTeacher && cameraLockEnabled}
            microphoneLocked={!isTeacher && microphoneLockEnabled}
            onToggleAudio={onToggleAudio}
            onToggleVideo={onToggleVideo}
            onToggleScreenShare={onToggleScreenShare}
            userMutedAudio={userMutedAudio}
            userMutedVideo={userMutedVideo}
            hasUserInteracted={hasUserInteracted}
            canScreenShare={!isTablet}
          />
          <div className="h-8 w-px bg-gray-600" />
          <button
            onClick={onToggleDocumentShare}
            className={`${btnPad} rounded-full transition-colors ${embeddedDocument ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}
            title={embeddedDocument ? t('controls.closeGoogleDoc') : t('controls.embedGoogleDoc')}
          >
            <DocumentTextIcon className="w-5 h-5" />
          </button>
          {isTeacher && (
            <button
              onClick={onToggleWhiteboard}
              disabled={screenSharing && !isWhiteboardDetached}
              className={`${btnPad} rounded-full transition-colors ${isWhiteboardDetached ? 'bg-indigo-600 hover:bg-indigo-700 text-white animate-pulse' : isWhiteboardActive ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : screenSharing ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}
              title={screenSharing && !isWhiteboardDetached ? t('controls.whiteboardDisabledScreenShare', 'Stop screen share to use whiteboard') : isWhiteboardDetached ? t('controls.reattachWhiteboard', 'Bring whiteboard back') : isWhiteboardActive ? t('controls.closeWhiteboard', 'Close Whiteboard') : t('controls.openWhiteboard', 'Open Whiteboard')}
            >
              <PencilSquareIcon className="w-5 h-5" />
            </button>
          )}
          <div className="h-8 w-px bg-gray-600" />
          <div className="relative" ref={settingsRef}>
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`${btnPad} rounded-full transition-colors ${settingsOpen ? 'bg-gray-600' : 'bg-gray-700 hover:bg-gray-600'} text-white`}
              title={t('controls.settings')}
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
            {settingsOpen && (
              <div className="absolute bottom-full left-0 mb-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-lg">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">{t('controls.sessionSettings')}</div>
                  {isTeacher && (
                    <>
                      <button onClick={() => { onToggleMediaLock('microphone'); setSettingsOpen(false); }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-gray-700 rounded transition-colors">
                        <span>{t('controls.locks.keepMicOn')}</span>
                        <div className={`w-5 h-5 rounded border ${microphoneLockEnabled ? 'bg-cyan-600 border-cyan-600' : 'border-gray-500'} flex items-center justify-center`}>
                          {microphoneLockEnabled && <CheckIcon className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                      <button onClick={() => { onToggleMediaLock('camera'); setSettingsOpen(false); }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-gray-700 rounded transition-colors">
                        <span>{t('controls.locks.keepCameraOn')}</span>
                        <div className={`w-5 h-5 rounded border ${cameraLockEnabled ? 'bg-cyan-600 border-cyan-600' : 'border-gray-500'} flex items-center justify-center`}>
                          {cameraLockEnabled && <CheckIcon className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                      <button onClick={() => { onToggleAutoAssignBreakoutRooms?.(); setSettingsOpen(false); }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-gray-700 rounded transition-colors">
                        <span className="flex items-center"><Squares2X2Icon className="w-4 h-4 mr-2" />{t('controls.autoAssignBreakoutRooms')}</span>
                        <div className={`w-5 h-5 rounded border ${autoAssignBreakoutRooms ? 'bg-cyan-600 border-cyan-600' : 'border-gray-500'} flex items-center justify-center`}>
                          {autoAssignBreakoutRooms && <CheckIcon className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                      <div className="h-px bg-gray-700 my-1" />
                    </>
                  )}
                  <button onClick={() => { onToggleHideOwnVideo(); setSettingsOpen(false); }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-gray-700 rounded transition-colors">
                    <span className="flex items-center"><EyeSlashIcon className="w-4 h-4 mr-2" />{t('controls.hideOwnVideo')}</span>
                    <div className={`w-5 h-5 rounded border ${hideOwnVideo ? 'bg-cyan-600 border-cyan-600' : 'border-gray-500'} flex items-center justify-center`}>
                      {hideOwnVideo && <CheckIcon className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                  <div className="h-px bg-gray-700 my-1" />
                  <button onClick={() => { localStorage.removeItem('simpleclass_audio_device'); localStorage.removeItem('simpleclass_video_device'); localStorage.removeItem('simpleclass_remember_device_choice'); toast.info(t('controls.deviceSettingsReset', 'Resetting audio & video settings...')); setSettingsOpen(false); setTimeout(() => window.location.reload(), 500); }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-gray-700 rounded transition-colors">
                    <span className="flex items-center"><ArrowPathIcon className="w-4 h-4 mr-2" />{t('controls.resetDeviceSettings', 'Reset audio/video settings')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          {!isTeacher && (cameraLockEnabled || microphoneLockEnabled) && (
            <div className="flex items-center space-x-1 ml-2 px-3 py-1 bg-yellow-900/30 rounded-full">
              <LockClosedIcon className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-yellow-500">{getLockText()}</span>
            </div>
          )}
        </div>
        <button
          onClick={onToggleSidebar}
          className={`${btnPad} relative rounded-full bg-gray-700 hover:bg-gray-600 transition-colors text-white`}
          title={sidebarCollapsed ? t('controls.showSidebar') : t('controls.hideSidebar')}
        >
          {sidebarCollapsed ? <ChevronLeftIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
          {sidebarCollapsed && unreadMessages > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-semibold animate-pulse">
              {unreadMessages > 99 ? '99+' : unreadMessages}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default SessionBottomBar;
